import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { CONTACTS as SEED_CONTACTS, DEALS as SEED_DEALS, TASKS_INIT as SEED_TASKS, STAGES as SEED_STAGES, STAGE_COLORS as SEED_STAGE_COLORS } from '../data/seed';
import { SYSTEM_FIELDS } from './systemFields';
import supabase from './supabase';
import { useToast } from '../components/Toast';
import { useAuth } from './auth';
import { checkAndFireWorkflows } from './workflowEngine';
import { apiFetch } from './api';
import { fmtDollar } from '../components/UI';

const StoreContext = createContext();
export const useStore = () => useContext(StoreContext);

/**
 * useFieldOptions — returns the effective options array for a Tier B system
 * field, applying override > default fallback.
 *
 *   const options = useFieldOptions("contacts", "source");
 *
 * Reactive to customFields state changes — when an admin saves an override
 * via setSystemFieldOptions, every consumer re-renders with the new list.
 *
 * Returns [] if the field doesn't exist or has no options. Cross-entity reuse
 * is allowed: "lead source" and "industry" are conceptually shared, so e.g.
 * DealRecord can call useFieldOptions("contacts", "source") and CompanyRecord
 * can call useFieldOptions("contacts", "industry").
 */
export function useFieldOptions(entity, fieldId) {
  const store = useStore();
  const override = store.customFields?.__overrides?.[entity]?.[fieldId]?.options;
  if (Array.isArray(override) && override.length > 0) return override;
  const field = (SYSTEM_FIELDS[entity] || []).find(f => f.id === fieldId);
  return field?.options || [];
}

// ── Audit log helper ──
const logAudit = async (entityType, entityId, entityName, changeAction, changes, fieldName, oldValue, newValue) => {
  try {
    await apiFetch("/api/audit", {
      method: "POST",
      body: JSON.stringify({
        action: "log", entityType, entityId, entityName,
        changeAction, changes, fieldName,
        oldValue: oldValue != null ? String(oldValue) : null,
        newValue: newValue != null ? String(newValue) : null,
        orgId: window.__coreflow_org_id,
      }),
    });
  } catch (e) { /* silent — audit should never block operations */ }
};

// ── Paginated fetch helper ──
// Fetches all rows of a query, paginating past PostgREST's 1000-row server cap
// (db-max-rows = 1000 in Supabase by default). The caller passes a function
// that returns a fresh query builder; the helper applies .range() per page
// until a partial page is returned. We need a function (not a pre-built query)
// because Supabase's PostgrestFilterBuilder is single-use — re-applying
// .range() to the same builder doesn't paginate, it just rewrites the header
// on a builder that's already been awaited.
//
// CONSISTENCY NOTE: when the caller's .order() column can change between page
// fetches (e.g. created_at + new rows being written), there's a small risk of
// duplicates or missed rows. For the auth-boot loader this is acceptable —
// loads run once at session start, not continuously. If we ever need fully
// consistent reads, switch to keyset pagination using created_at as a cursor
// instead of OFFSET-style .range().
export async function fetchAllPaginated(queryBuilder, pageSize = 1000) {
  let all = [];
  let from = 0;
  while (true) {
    const { data, error } = await queryBuilder().range(from, from + pageSize - 1);
    if (error) throw error;
    if (!data) break;
    all = all.concat(data);
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

const cfDateTime = (d) => { if (!d) return "—"; const dt = new Date(d); const m = String(dt.getMonth()+1).padStart(2,"0"); const dy = String(dt.getDate()).padStart(2,"0"); const y = String(dt.getFullYear()).slice(-2); let h = dt.getHours(); const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12; const mn = String(dt.getMinutes()).padStart(2,"0"); return m+"/"+dy+"/"+y+" "+h+":"+mn+" "+ap; };
const cfDate = (d) => { if (!d) return "—"; const dt = new Date(d); const m = String(dt.getMonth()+1).padStart(2,"0"); const dy = String(dt.getDate()).padStart(2,"0"); const y = String(dt.getFullYear()).slice(-2); return m+"/"+dy+"/"+y; };

// ── Supabase data adapters ──
function dbContactToLocal(row) {
  return {
    id: row.id, fn: row.first_name, ln: row.last_name,
    email: row.email || "", ph: row.phone || "", co: row.company || "",
    company_id: row.company_id || "",
    title: row.job_title || "", source: row.lead_source || "Inbound",
    leadStage: row.lead_stage || "new", type: row.account_type || "SMB",
    industry: row.industry || "", city: row.city || "", state: row.state || "",
    score: row.score || 0, status: row.status || "active",
    tags: row.tags || [], ai: row.ai_summary || "",
    sent: row.sentiment || "neutral",
    ownerId: row.owner_id || "", owner: row.owner_id || "",
    last_disposition_id: row.last_disposition_id || null,
    do_not_call: !!row.do_not_call,
    qualified_at: row.qualified_at || null,
    requeue_after: row.requeue_after || null,
    properties: row.properties || {},
    last: row.last_activity_at ? new Date(row.last_activity_at).toISOString().slice(0, 10) : "",
    lastActivity: row.last_activity_at ? new Date(row.last_activity_at).toISOString().slice(0, 10) : "",
    created: row.created_at ? new Date(row.created_at).toISOString().slice(0, 10) : "",
    activities: [], _dbId: row.id, _orgId: row.org_id,
    ...Object.fromEntries(Object.entries(row.properties || {}).filter(([k]) => !["id","fn","ln","email","ph","co","company_id","title","source","leadStage","type","industry","city","state","score","status","tags","ai","sent","ownerId","owner","properties","last","lastActivity","created","activities","_dbId","_orgId"].includes(k))),
  };
}

function dbDealToLocal(row) {
  // Calculate risk from days_in_stage
  const days = row.days_in_stage || 0;
  const risk = days >= 21 ? "high" : days >= 14 ? "medium" : "low";
  return {
    id: row.id, name: row.name, val: Number(row.value) || 0,
    stage: row.stage || "Discovery", prob: row.probability || 0,
    days: days, ct: row.contact_id || "", co: "",
    company_id: row.company_id || "",
    ownerId: row.owner_id || "", owner: row.owner_id || "", ai: row.ai_summary || "",
    risk: row.risk || (row.stage === "Closed Won" ? "none" : risk),
    close: row.close_date ? new Date(row.close_date).toISOString().slice(0, 10) : "",
    created: row.created_at ? new Date(row.created_at).toISOString().slice(0, 10) : "",
    properties: row.properties || {}, _dbId: row.id, _orgId: row.org_id,
    ...Object.fromEntries(Object.entries(row.properties || {}).filter(([k]) => !["id","name","val","stage","prob","ct","co","company_id","close","created","days","risk","ai","ownerId","properties","_dbId","_orgId"].includes(k))),
  };
}

function dbTaskToLocal(row) {
  return {
    id: row.id, title: row.title, done: row.status === "done",
    description: row.description || "",
    due: row.due_date || "", pri: row.priority || "medium",
    ct: row.contact_id || "", deal: row.deal_id || "",
    assignee: row.assigned_to || "", _dbId: row.id,
    properties: row.properties || {},
    ...Object.fromEntries(Object.entries(row.properties || {}).filter(([k]) => !["id","title","done","due","pri","ct","deal","assignee","description","_dbId","properties"].includes(k))),
  };
}

function dbActivityToLocal(row) {
  return {
    id: row.id, type: row.type, title: row.title,
    notes: row.notes || "",
    date: row.created_at ? new Date(row.created_at).toISOString().slice(0, 16).replace("T", " ") : "",
    // Raw ISO preserved so timezone-aware date bucketing (My Activity
    // panel) doesn't have to reverse-parse the truncated `date` string.
    created_at: row.created_at || "",
    contactId: row.contact_id || "", dealId: row.deal_id || "",
    companyId: row.company_id || null,
    by: row.user_id || "", duration: row.duration || "",
    properties: row.properties || {},
    ...Object.fromEntries(Object.entries(row.properties || {}).filter(([k]) => !["id","type","title","notes","date","contactId","dealId","companyId","by","duration","properties"].includes(k))),
  };
}

function dbCompanyToLocal(row) {
  return {
    id: row.id,
    name: row.name || "",
    domain: row.domain || "",
    industry: row.industry || "",
    size: row.size || "",
    annualRevenue: row.annual_revenue != null ? Number(row.annual_revenue) : null,
    city: row.city || "",
    state: row.state || "",
    country: row.country || "",
    phone: row.phone || "",
    description: row.description || "",
    ai: row.ai_summary || "",
    ownerId: row.owner_id || "",
    owner: row.owner_id || "",
    tags: row.tags || [],
    properties: row.properties || {},
    created: row.created_at ? new Date(row.created_at).toISOString().slice(0, 10) : "",
    _dbId: row.id,
    _orgId: row.org_id,
  };
}

// ── Defaults ──
const SEED_PIPELINE = SEED_STAGES.map((s, i) => ({ id: i, name: s, color: SEED_STAGE_COLORS[s], prob: [20, 40, 60, 80, 100][i] || 50 }));
const SEED_LEAD_STAGES = [
  { id: 0, name: "New", color: "#535C6E" },
  { id: 1, name: "Qualified", color: "#FFB547" },
  { id: 2, name: "Proposal", color: "#7C5CFC" },
  { id: 3, name: "Negotiation", color: "#FAFAFA" },
  { id: 4, name: "Customer", color: "#00D68F" },
];
// Team loaded from DB via auth.users

const DEFAULT_ORG_ID = null; // No fallback — requires authenticated org

// Get org_id from authenticated user — no fallback, must be set via auth
function getOrgId() {
  return window.__coreflow_org_id || null;
}

export function StoreProvider({ children }) {
  const toast = useToast();
  const auth = useAuth();
  const orgId = auth?.currentUser?._orgId || null;
  const saveToastTimer = useRef(null);
  const showSaved = useCallback(() => {
    if (saveToastTimer.current) clearTimeout(saveToastTimer.current);
    saveToastTimer.current = setTimeout(() => toast?.success("Saved"), 300);
  }, [toast]);
  const [contacts, setContacts] = useState([]);
  const contactsRef = useRef(contacts);
  contactsRef.current = contacts;
  const [deals, setDeals] = useState([]);
  const dealsRef = useRef(deals);
  dealsRef.current = deals;
  const [companies, setCompanies] = useState([]);
  const companiesRef = useRef(companies);
  companiesRef.current = companies;
  // email_connections roster (no tokens) + per-row shared_with grants.
  // Used by EmailPage to compute the inbox-access boundary and by
  // AdminCenter's Email Access tab to manage grants.
  const [emailConnections, setEmailConnections] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [callQueue, setCallQueueRaw] = useState([]);
  const [savedViews, setSavedViewsRaw] = useState([]);
  const [activities, setActivities] = useState([]);
  const [dealContacts, setDealContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dbReady, setDbReady] = useState(false);

  const [teamMembers, setTeamMembers] = useState([]);
  const updateTeamMember = useCallback((id, updates) => {
    setTeamMembers(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
  }, []);
  // Resolve current user from auth session, not array position
  const [resolvedUserId, setResolvedUserId] = useState(null);
  const currentUserMember = useMemo(() => {
    // First try: match by resolved user ID from DB
    if (resolvedUserId) {
      const match = teamMembers.find(m => m._dbId === resolvedUserId);
      if (match) return match;
    }
    // Second try: match by auth email (most reliable)
    const authEmail = (window.__coreflow_auth_email || "").toLowerCase();
    if (authEmail) {
      const match = teamMembers.find(m => m.email?.toLowerCase() === authEmail);
      if (match) return match;
    }
    // Last resort: first member (but this is wrong if multiple users)
    return teamMembers[0];
  }, [teamMembers, resolvedUserId]);
  const currentUser = currentUserMember?.name || "You";
  const currentUserId = currentUserMember?._dbId || null;

  // Resolve owner UUID to display name
  const getOwnerName = useCallback((ownerId) => {
    if (!ownerId) return "—";
    const member = teamMembers.find(m => m._dbId === ownerId);
    return member ? member.name : ownerId;
  }, [teamMembers]);

  const [pipelineStages, setPipelineStagesRaw] = useState(() => {
    try { const saved = localStorage.getItem("cf_pipeline_stages_" + getOrgId()); return saved ? JSON.parse(saved) : SEED_PIPELINE; }
    catch { return SEED_PIPELINE; }
  });
  const setPipelineStages = useCallback((val) => {
    setPipelineStagesRaw(prev => {
      const next = typeof val === "function" ? val(prev) : val;
      try { localStorage.setItem("cf_pipeline_stages_" + getOrgId(), JSON.stringify(next)); } catch {}
      // Also persist to Supabase
      const orgId = getOrgId();
      if (orgId && orgId !== "default") {
        supabase.from("organizations").update({ pipeline_stages: next }).eq("id", orgId).then(() => {});
      }
      return next;
    });
  }, []);
  const stages = pipelineStages.map(s => s.name);
  const stageColors = pipelineStages.reduce((acc, s) => { acc[s.name] = s.color; return acc; }, {});

  const [leadStages, setLeadStagesRaw] = useState(() => {
    try { const saved = localStorage.getItem("cf_lead_stages_" + getOrgId()); return saved ? JSON.parse(saved) : SEED_LEAD_STAGES; }
    catch { return SEED_LEAD_STAGES; }
  });
  const setLeadStages = useCallback((val) => {
    setLeadStagesRaw(prev => {
      const next = typeof val === "function" ? val(prev) : val;
      try { localStorage.setItem("cf_lead_stages_" + getOrgId(), JSON.stringify(next)); } catch {}
      const orgId = getOrgId();
      if (orgId && orgId !== "default") {
        supabase.from("organizations").update({ lead_stages: next }).eq("id", orgId).then(() => {});
      }
      return next;
    });
  }, []);

  const [customPipelines, setCustomPipelines] = useState([]);
  const [customPipelineItems, setCustomPipelineItems] = useState([]);
  
  // Governance settings
  const [governanceSettings, setGovernanceSettings] = useState({});
  const [requiredContactFields, setRequiredContactFields] = useState([]);
  const [requiredDealFields, setRequiredDealFields] = useState([]);
  const [stageGates, setStageGates] = useState({});
  const addPipeline = useCallback((p) => { setCustomPipelines(prev => [...prev, { id: Date.now(), items: [], ...p }]); }, []);
  const updatePipeline = useCallback((id, u) => { setCustomPipelines(prev => prev.map(p => p.id === id ? { ...p, ...u } : p)); }, []);
  const removePipeline = useCallback((id) => { setCustomPipelines(prev => prev.filter(p => p.id !== id)); setCustomPipelineItems(prev => prev.filter(i => i.pipelineId !== id)); }, []);
  const addPipelineItem = useCallback((item) => { const n = { id: Date.now(), created: cfDate(new Date()), ...item }; setCustomPipelineItems(prev => [...prev, n]); return n; }, []);
  const updatePipelineItem = useCallback((id, u) => { setCustomPipelineItems(prev => prev.map(i => i.id === id ? { ...i, ...u } : i)); }, []);
  const removePipelineItem = useCallback((id) => { setCustomPipelineItems(prev => prev.filter(i => i.id !== id)); }, []);

  const [customFields, setCustomFieldsRaw] = useState(() => {
    try { const saved = localStorage.getItem("cf_custom_fields_" + getOrgId()); return saved ? JSON.parse(saved) : { contacts: [], deals: [], companies: [], tasks: [], calls: [], emails: [], sms: [], calendar: [] }; }
    catch { return { contacts: [], deals: [], companies: [], tasks: [], calls: [], emails: [], sms: [], calendar: [] }; }
  });
  const setCustomFields = useCallback((val) => {
    setCustomFieldsRaw(prev => {
      const next = typeof val === "function" ? val(prev) : val;
      try { localStorage.setItem("cf_custom_fields_" + getOrgId(), JSON.stringify(next)); } catch {}
      const orgId = getOrgId();
      if (orgId && orgId !== "default") {
        supabase.from("organizations").update({ custom_fields: next }).eq("id", orgId).then(() => {});
      }
      return next;
    });
  }, []);
  const addCustomField = useCallback((entity, field) => { setCustomFields(prev => ({ ...prev, [entity]: [...(prev[entity] || []), field] })); }, []);
  const removeCustomField = useCallback((entity, fid) => { setCustomFields(prev => ({ ...prev, [entity]: (prev[entity] || []).filter(f => f.id !== fid) })); }, []);
  const updateCustomField = useCallback((entity, fid, u) => { setCustomFields(prev => ({ ...prev, [entity]: (prev[entity] || []).map(f => f.id === fid ? { ...f, ...u } : f) })); }, []);
  // Tier B: persist a per-org override of a system field's `options` array.
  // Stored under customFields.__overrides[entity][fieldId] to keep the shape
  // distinct from custom-field definitions (entity arrays). Encapsulates the
  // storage shape so callers stay agnostic — readers go through
  // getEffectiveOptions in AdminCenter, and consumer code in Commit I.
  const setSystemFieldOptions = useCallback((entity, fieldId, options) => {
    setCustomFields(prev => ({
      ...prev,
      __overrides: {
        ...(prev.__overrides || {}),
        [entity]: {
          ...((prev.__overrides || {})[entity] || {}),
          [fieldId]: { options },
        },
      },
    }));
  }, []);

  // ── Call Dispositions (org-scoped, admin-editable, RLS-scoped) ──
  // Backed by the call_dispositions table created in migration 071.
  // Each disposition has a label, a behavior (one of 6 fixed values
  // enforced by CHECK constraint), and an optional requeue_days
  // (only meaningful when behavior='requeue_later'). v1 admin UI
  // surfaces 5 of the 6 behaviors (mark_closed_lost is reserved
  // forward-compat). The initial fetch is wired into the boot-time
  // org-config load below; the CRUD helpers maintain local state
  // in sync with INSERT/UPDATE results.
  const [callDispositions, setCallDispositionsRaw] = useState([]);

  const addCallDisposition = useCallback(async (payload) => {
    const orgId = getOrgId();
    if (!orgId) throw new Error("No org");
    const maxSort = (callDispositions || []).reduce((m, d) => Math.max(m, d.sort_order || 0), 0);
    const { data, error } = await supabase
      .from("call_dispositions")
      .insert({
        org_id: orgId,
        label: payload.label,
        behavior: payload.behavior,
        requeue_days: payload.behavior === "requeue_later" ? (payload.requeue_days || 3) : null,
        sort_order: maxSort + 1,
        enabled: true,
      })
      .select()
      .single();
    if (error) {
      console.error("[CoreFlow] addCallDisposition failed:", error.message);
      throw error;
    }
    setCallDispositionsRaw(prev => [...prev, data].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    return data;
  }, [callDispositions]);

  const updateCallDisposition = useCallback(async (id, payload) => {
    const updates = {
      label: payload.label,
      behavior: payload.behavior,
      requeue_days: payload.behavior === "requeue_later" ? (payload.requeue_days || 3) : null,
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase
      .from("call_dispositions")
      .update(updates)
      .eq("id", id)
      .select()
      .single();
    if (error) {
      console.error("[CoreFlow] updateCallDisposition failed:", error.message);
      throw error;
    }
    setCallDispositionsRaw(prev => prev.map(d => (d.id === id ? data : d)));
    return data;
  }, []);

  const toggleCallDisposition = useCallback(async (id, enabled) => {
    const { data, error } = await supabase
      .from("call_dispositions")
      .update({ enabled, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();
    if (error) {
      console.error("[CoreFlow] toggleCallDisposition failed:", error.message);
      throw error;
    }
    setCallDispositionsRaw(prev => prev.map(d => (d.id === id ? data : d)));
    return data;
  }, []);

  // ── Apply a disposition's behavior to a contact + persist to call_logs ──
  // Called from DialerPage.handleDisposition after the user picks a disposition.
  // Three writes:
  //   1. UPDATE call_logs SET disposition_id WHERE id = callLogId  (FK persists)
  //   2. UPDATE contacts SET last_disposition_id + behavior-specific flags
  //   3. Mirror the contact updates into local React state
  //
  // Behavior mapping (matches BEHAVIOR_LABELS in AdminCenter.js):
  //   none           → no contact-side update
  //   do_not_call    → do_not_call=true (Power Dialer excludes)
  //   qualified      → qualified_at=NOW (Power Dialer excludes, manual still works)
  //   wrong_number   → properties.bad_phone=true (non-destructive flag)
  //   requeue_later  → requeue_after = NOW + (disposition.requeue_days * 86400000)ms
  //
  // mark_closed_lost is NOT handled here in v1 (admin UI doesn't surface it).
  // If it ever does, add the lead_stage write here.
  //
  // Each individual DB write is independent — a failure in one doesn't roll
  // back the others. Caller treats partial failure as best-effort. Errors are
  // logged but not thrown (the call has already happened from the user's
  // perspective; failing the disposition write loudly would just confuse).
  const applyDisposition = useCallback(async (contactId, disposition, callLogId) => {
    if (!contactId || !disposition?.id) return { ok: false, error: "missing_args" };

    // 1. Persist disposition_id on the call_logs row (created by callLogger
    //    earlier in the call lifecycle). Skipped silently if no callLogId
    //    (e.g., logCallToDb failed — disposition still applies to contact).
    if (callLogId) {
      const { error: clErr } = await supabase
        .from("call_logs")
        .update({ disposition_id: disposition.id })
        .eq("id", callLogId);
      if (clErr) console.warn("[CoreFlow] call_logs disposition_id update failed:", clErr.message);
    }

    // 2. Build the contacts UPDATE based on behavior. last_disposition_id is
    //    always set; flag updates are additive (we don't clear other flags
    //    when applying a new disposition — admin can manually clear via
    //    contact record edit if needed).
    const contactUpdates = {
      last_disposition_id: disposition.id,
      updated_at: new Date().toISOString(),
    };
    let propertiesPatch = null;
    switch (disposition.behavior) {
      case "do_not_call":
        contactUpdates.do_not_call = true;
        break;
      case "qualified":
        contactUpdates.qualified_at = new Date().toISOString();
        break;
      case "wrong_number":
        // Non-destructive: keep phone for audit, mark via properties.bad_phone.
        propertiesPatch = { bad_phone: true };
        break;
      case "requeue_later": {
        const days = Math.max(1, Math.min(365, disposition.requeue_days || 3));
        contactUpdates.requeue_after = new Date(Date.now() + days * 86400000).toISOString();
        break;
      }
      // 'none' and unknown behaviors fall through with just last_disposition_id
    }

    // 3. wrong_number's properties JSONB merge needs the existing properties
    //    so we don't clobber custom fields. Read-then-write is acceptable
    //    here — single contact, fast.
    if (propertiesPatch) {
      const { data: existingC } = await supabase
        .from("contacts")
        .select("properties")
        .eq("id", contactId)
        .maybeSingle();
      contactUpdates.properties = { ...(existingC?.properties || {}), ...propertiesPatch };
    }

    const { error: cErr } = await supabase
      .from("contacts")
      .update(contactUpdates)
      .eq("id", contactId);
    if (cErr) {
      console.warn("[CoreFlow] contact disposition update failed:", cErr.message);
      return { ok: false, error: cErr.message };
    }

    // 4. Local state mirror — keep ContactsList / Power Dialer in sync without
    //    a re-fetch. Mirrors the same fields we wrote to the DB.
    setContacts(prev => prev.map(c => {
      if (c.id !== contactId) return c;
      const next = { ...c, last_disposition_id: disposition.id };
      if (disposition.behavior === "do_not_call") next.do_not_call = true;
      if (disposition.behavior === "qualified") next.qualified_at = contactUpdates.qualified_at;
      if (disposition.behavior === "wrong_number") next.properties = contactUpdates.properties;
      if (disposition.behavior === "requeue_later") next.requeue_after = contactUpdates.requeue_after;
      return next;
    }));

    return { ok: true };
  }, []);

  // ── KPI Targets (per role, set by manager) ──
  const DEFAULT_KPI = {
    sdr: { dials: 150, emails: 50, linkedin_dms: 10, label: "SDR" },
    account_exec: { dials: 30, emails: 25, linkedin_dms: 5, label: "Account Exec" },
    manager: { dials: 10, emails: 15, linkedin_dms: 3, label: "Manager" },
    customer_success: { dials: 20, emails: 30, linkedin_dms: 5, label: "Customer Success" },
    admin: { dials: 10, emails: 15, linkedin_dms: 3, label: "Admin" },
    viewer: { dials: 0, emails: 0, linkedin_dms: 0, label: "Viewer" },
  };
  const [kpiTargets, setKpiTargetsRaw] = useState(() => {
    try { const saved = localStorage.getItem("cf_kpi_targets_" + getOrgId()); return saved ? JSON.parse(saved) : DEFAULT_KPI; }
    catch { return DEFAULT_KPI; }
  });
  const setKpiTargets = useCallback((val) => {
    setKpiTargetsRaw(prev => {
      const next = typeof val === "function" ? val(prev) : val;
      try { localStorage.setItem("cf_kpi_targets_" + getOrgId(), JSON.stringify(next)); } catch {}
      const orgId = getOrgId();
      if (orgId && orgId !== "default") {
        supabase.from("organizations").update({ kpi_targets: next }).eq("id", orgId).then(() => {});
      }
      return next;
    });
  }, []);

  // ═══════════════════════════════════════════
  // SUPABASE: Load on startup
  // Reactive to orgId from auth context — effect re-fires when orgId arrives.
  // Known narrow limitation: if orgId changes mid-load (org-switch scenario),
  // the stale load() can still write its results after the new load() starts.
  // Not a real-world concern for trial launch; AbortController fix is a
  // separate follow-up.
  // ═══════════════════════════════════════════
  useEffect(() => {
    // No org yet (session shell, pre-profile, or signed out). Wait for orgId
    // to arrive; effect will re-fire automatically. Keep loading=true so UI
    // shows skeletons rather than "no data".
    if (!orgId) {
      setContacts([]); setDeals([]); setTasks([]); setCompanies([]);
      setEmailConnections([]);
      setCallQueueRaw([]); setActivities([]); setTeamMembers([]);
      setResolvedUserId(null); setSavedViewsRaw([]);
      setDbReady(false);
      setLoading(true);
      return;
    }

    let done = false;

    // Safety net for the auth-boot loader. Bumped from 6s to 15s after the
    // PostgREST 1000-row pagination fix (aa1c5fef) made the load multi-round-
    // trip for orgs with >1000 contacts/deal_contacts.
    //
    // The timeout no longer wipes loaded state. The previous behavior reset
    // contacts/deals/tasks/companies/email_connections to [] when it fired —
    // and it was firing AFTER setContacts/setActivities had already painted,
    // because `done` only flipped in the finally block (which waits for
    // secondary loaders: call_queue, saved_views). That zeroed perfectly
    // good data. Now the timeout only releases the loading spinner;
    // whatever primary loaders completed before T+15s stay visible.
    //
    // Cleared explicitly right after the primary loaders finish (see below)
    // so secondary loaders don't leave the timeout pending.
    const timeout = setTimeout(() => {
      if (!done) {
        done = true;
        console.warn("[CoreFlow] Load timeout — secondary loaders still running, releasing UI");
        setLoading(false);
      }
    }, 15000);

    async function load() {
      try {
        console.log("[CoreFlow] Starting data load...");

        // Load each table individually so one failure doesn't block all
        let lc = [], ld = [], lt = [], la = [], lco = [];

        try {
          const data = await fetchAllPaginated(() =>
            supabase.from("contacts").select("*").eq("org_id", orgId).order("created_at", { ascending: false })
          );
          lc = data.map(dbContactToLocal);
          console.log("[CoreFlow] Contacts:", lc.length);
        } catch (e) { console.warn("[CoreFlow] Contacts load failed:", e.message); toast?.warning("Failed to load contacts"); }

        try {
          const data = await fetchAllPaginated(() =>
            supabase.from("deals").select("*").eq("org_id", orgId).order("created_at", { ascending: false })
          );
          ld = data.map(dbDealToLocal);
          console.log("[CoreFlow] Deals:", ld.length);
        } catch (e) { console.warn("[CoreFlow] Deals load failed:", e.message); toast?.warning("Failed to load deals"); }

        try {
          const data = await fetchAllPaginated(() =>
            supabase.from("companies").select("*").eq("org_id", orgId).order("created_at", { ascending: false })
          );
          lco = data.map(dbCompanyToLocal);
          console.log("[CoreFlow] Companies:", lco.length);
        } catch (e) { console.warn("[CoreFlow] Companies load failed:", e.message); }

        // Email connections roster — no tokens, just the metadata + shared_with
        // grants used for the inbox-access boundary. Set inline via setState here
        // so it lands at the same point as the other org-scoped data.
        try {
          const { data, error } = await supabase.from("email_connections")
            .select("id, user_id, provider, email, display_name, is_active, shared_with, created_at")
            .eq("org_id", orgId);
          if (!error && data) {
            setEmailConnections(data);
            console.log("[CoreFlow] Email connections:", data.length);
          }
        } catch (e) { console.warn("[CoreFlow] Email connections load failed:", e.message); }

        try {
          const data = await fetchAllPaginated(() =>
            supabase.from("tasks").select("*").eq("org_id", orgId).order("created_at", { ascending: false })
          );
          lt = data.map(dbTaskToLocal);
          console.log("[CoreFlow] Tasks:", lt.length);
        } catch (e) { console.warn("[CoreFlow] Tasks load failed:", e.message); toast?.warning("Failed to load tasks"); }

        try {
          const { data, error } = await supabase.from("activities").select("*").eq("org_id", orgId).order("created_at", { ascending: false }).limit(200);
          if (!error && data) la = data.map(dbActivityToLocal);
          console.log("[CoreFlow] Activities:", la.length);
        } catch (e) { console.warn("[CoreFlow] Activities load failed:", e.message); }

        // Per-user activities for the My Activity panel. The org-wide
        // fetch above is capped at 200 so contact/deal/company record
        // pages have a reasonable cross-user timeline; that cap means
        // a busy org can starve the current user's panel of their own
        // rows. Issue a parallel user-scoped fetch with no cap and
        // merge it into `la` (deduped by id) so that single-store
        // consumers (record pages + panel filter) see both views.
        // The user-scoped fetch happens after we've resolved
        // loadedUserId (via the users load below) so we know which
        // user_id to filter on. We compute it here as a no-op block;
        // the actual fetch runs in the section after user resolution.

        try {
          // Order by created_at so .range() pagination is stable across pages.
          const data = await fetchAllPaginated(() =>
            supabase.from("deal_contacts").select("*").eq("org_id", orgId).order("created_at", { ascending: false })
          );
          setDealContacts(data || []);
          console.log("[CoreFlow] Deal contacts:", (data || []).length);
        } catch (e) {
          console.warn("[CoreFlow] deal_contacts load failed (treating as empty):", e?.message);
          setDealContacts([]);
        }

        let loadedUserId = null;
        try {
          const { data } = await supabase.from("users").select("*").eq("org_id", orgId);
          if (data?.length > 0) {
            setTeamMembers(data.map((u, i) => ({
              id: i + 1, name: u.first_name + " " + u.last_name, role: u.role,
              email: u.email, status: u.is_active ? "active" : "inactive",
              avatar: u.avatar || (u.first_name[0] + u.last_name[0]), quota: Number(u.quota) || 0, _dbId: u.id,
              avatarUrl: u.avatar_url || null,
              timezone: u.timezone || "America/New_York",
            })));
            // Match current user: auth_id first (most reliable), then email
            const { data: { session } } = await supabase.auth.getSession();
            const authId = session?.user?.id;
            const authEmail = (session?.user?.email || window.__coreflow_auth_email || "").toLowerCase();
            window.__coreflow_auth_email = authEmail;
            
            const byAuthId = authId ? data.find(u => u.auth_id === authId) : null;
            const byEmail = authEmail ? data.find(u => u.email?.toLowerCase() === authEmail) : null;
            loadedUserId = byAuthId?.id || byEmail?.id || data[0]?.id;
            setResolvedUserId(loadedUserId);
            console.log("[CoreFlow] Auth email:", authEmail, "authId:", authId, "loadedUserId:", loadedUserId,
              "matched:", byAuthId ? "by auth_id" : byEmail ? "by email" : "fallback");
          }
        } catch (e) { console.warn("[CoreFlow] Users load failed:", e.message); }

        // Per-user activities top-up (Layer 2 of My Activity fix).
        // Now that loadedUserId is known, fetch ALL of this user's
        // activities and merge into `la` so the My Activity panel sees
        // a complete history regardless of the 200-row org-wide cap.
        if (loadedUserId) {
          try {
            const myData = await fetchAllPaginated(() =>
              supabase.from("activities")
                .select("*")
                .eq("org_id", orgId)
                .eq("user_id", loadedUserId)
                .order("created_at", { ascending: false })
            );
            const myActs = (myData || []).map(dbActivityToLocal);
            const existingIds = new Set(la.map(a => a.id));
            const additions = myActs.filter(a => !existingIds.has(a.id));
            // Prepend additions then re-sort by date desc to keep
            // record-page consumers' assumed ordering intact.
            la = [...additions, ...la].sort((x, y) => (y.date || "").localeCompare(x.date || ""));
            console.log("[CoreFlow] My activities (per-user fetch):", myActs.length, "new merged:", additions.length);
          } catch (e) { console.warn("[CoreFlow] My activities load failed:", e.message); }
        }

        // Load org config (pipeline stages, lead stages, custom fields)
        try {
          const { data: orgConfig } = await supabase.from("organizations")
            .select("pipeline_stages, lead_stages, custom_fields, kpi_targets, required_contact_fields, required_deal_fields, stage_gates, governance_settings")
            .eq("id", orgId).maybeSingle();
          if (orgConfig) {
            if (orgConfig.pipeline_stages && Array.isArray(orgConfig.pipeline_stages) && orgConfig.pipeline_stages.length > 0) {
              setPipelineStagesRaw(orgConfig.pipeline_stages);
              localStorage.setItem("cf_pipeline_stages_" + orgId, JSON.stringify(orgConfig.pipeline_stages));
              console.log("[CoreFlow] Pipeline stages loaded from DB:", orgConfig.pipeline_stages.length);
            } else {
              // First time — seed DB with defaults so all users get the same stages
              console.log("[CoreFlow] No pipeline stages in DB — seeding defaults");
              supabase.from("organizations").update({ pipeline_stages: SEED_PIPELINE }).eq("id", orgId).then(() => {});
            }
            if (orgConfig.lead_stages && Array.isArray(orgConfig.lead_stages) && orgConfig.lead_stages.length > 0) {
              setLeadStagesRaw(orgConfig.lead_stages);
              localStorage.setItem("cf_lead_stages_" + orgId, JSON.stringify(orgConfig.lead_stages));
              console.log("[CoreFlow] Lead stages loaded from DB:", orgConfig.lead_stages.length);
            } else {
              supabase.from("organizations").update({ lead_stages: SEED_LEAD_STAGES }).eq("id", orgId).then(() => {});
            }
            if (orgConfig.custom_fields && typeof orgConfig.custom_fields === "object") {
              setCustomFieldsRaw(orgConfig.custom_fields);
              localStorage.setItem("cf_custom_fields_" + orgId, JSON.stringify(orgConfig.custom_fields));
              console.log("[CoreFlow] Custom fields loaded from DB");
            }
            if (orgConfig.kpi_targets && typeof orgConfig.kpi_targets === "object") {
              setKpiTargetsRaw(orgConfig.kpi_targets);
              localStorage.setItem("cf_kpi_targets_" + orgId, JSON.stringify(orgConfig.kpi_targets));
              console.log("[CoreFlow] KPI targets loaded from DB");
            }
            // Governance settings
            if (orgConfig.governance_settings) setGovernanceSettings(orgConfig.governance_settings);
            if (orgConfig.required_contact_fields) setRequiredContactFields(orgConfig.required_contact_fields);
            if (orgConfig.required_deal_fields) setRequiredDealFields(orgConfig.required_deal_fields);
            if (orgConfig.stage_gates) setStageGates(orgConfig.stage_gates);
          }
        } catch (e) { console.warn("[CoreFlow] Org config load failed:", e.message); }

        // Call Dispositions — separate table (migration 071), not a JSONB
        // column, so it gets its own query. RLS scopes by get_user_org_id().
        // Failure is non-fatal — admin can still configure later; the dialer
        // falls back to whatever's already cached.
        try {
          const { data: cdData, error: cdErr } = await supabase
            .from("call_dispositions")
            .select("*")
            .order("sort_order", { ascending: true });
          if (cdErr) console.warn("[CoreFlow] Call dispositions load failed:", cdErr.message);
          else if (cdData) {
            setCallDispositionsRaw(cdData);
            console.log("[CoreFlow] Call dispositions loaded:", cdData.length);
          }
        } catch (e) { console.warn("[CoreFlow] Call dispositions load failed:", e.message); }

        // Map activities to contacts
        const byC = {};
        la.forEach(a => { if (a.contactId) { if (!byC[a.contactId]) byC[a.contactId] = []; byC[a.contactId].push(a); } });
        lc.forEach(c => { c.activities = byC[c.id] || []; });

        setContacts(lc);
        setDeals(ld);
        setCompanies(lco);
        setActivities(la);

        // Tasks loaded from DB (no seeding for new orgs)

        setTasks(lt);
        setDbReady(true);
        console.log("[CoreFlow] Loaded:", lc.length, "contacts,", ld.length, "deals");

        // Primary loaders done. Release the UI now so secondary loaders
        // (call_queue, saved_views) don't keep the user looking at a spinner
        // — they'll backfill state when they finish. Also cancel the safety
        // timeout so it doesn't fire spuriously while secondary loads run.
        if (!done) { done = true; setLoading(false); clearTimeout(timeout); }

        // Load call queue for current user
        if (loadedUserId) {
          try {
            const { data: qData } = await supabase.from("call_queue_items")
              .select("*").eq("user_id", loadedUserId).eq("status", "pending")
              .order("position", { ascending: true });
            if (qData?.length > 0) {
              setCallQueueRaw(qData.map(r => ({
                id: r.id, ct: r.contact_id, pri: r.priority, reason: r.reason,
                attempts: r.attempts, best: r.best_time, _dbId: r.id,
              })));
              console.log("[CoreFlow] Queue:", qData.length);
            }
          } catch (e) { console.warn("[CoreFlow] Queue load failed:", e.message); }

          // Load saved views for current user
          try {
            const { data: vData, error: vErr } = await supabase.from("saved_views")
              .select("*").eq("user_id", loadedUserId)
              .order("position", { ascending: true });
            console.log("[CoreFlow] Views query for userId:", loadedUserId, "result:", vData?.length || 0, "error:", vErr?.message || "none");
            if (vData?.length > 0) {
              setSavedViewsRaw(vData.map(r => ({
                id: r.id, name: r.name, icon: r.icon, filters: r.filters,
                entity: r.entity, isShared: r.is_shared, _dbId: r.id,
              })));
              console.log("[CoreFlow] Views:", vData.length);
            }
          } catch (e) { console.warn("[CoreFlow] Views load failed:", e.message); }
        }
      } catch (err) {
        console.warn("[CoreFlow] Full load failed:", err.message);
        setContacts([]); setDeals([]); setTasks([]); setCompanies([]);
        setEmailConnections([]);
      } finally {
        // Defensive — only fires if the primary-loaders cancel above didn't
        // run (e.g., a synchronous throw before that point). Normal happy
        // path already flipped `done` and cleared the timeout.
        if (!done) { done = true; setLoading(false); clearTimeout(timeout); }
      }
    }

    setLoading(true);
    load();

    return () => clearTimeout(timeout);
  }, [orgId]);

  // ═══════════════════════════════════════════
  // SUPABASE: Write-back helpers
  // ═══════════════════════════════════════════
  const dbUpdateContact = useCallback(async (id, updates) => {
    if (!dbReady) return false;
    const m = {};
    const knownKeys = new Set(["fn","ln","email","ph","co","title","source","leadStage","type","industry","city","state","score","status","ai","properties","owner","tags","activities"]);
    const customProps = {};
    if (updates.fn !== undefined) m.first_name = updates.fn;
    if (updates.ln !== undefined) m.last_name = updates.ln;
    if (updates.email !== undefined) m.email = updates.email;
    if (updates.ph !== undefined) m.phone = updates.ph;
    if (updates.co !== undefined) m.company = updates.co;
    if (updates.title !== undefined) m.job_title = updates.title;
    if (updates.source !== undefined) m.lead_source = updates.source;
    if (updates.leadStage !== undefined) m.lead_stage = updates.leadStage;
    if (updates.type !== undefined) m.account_type = updates.type;
    if (updates.industry !== undefined) m.industry = updates.industry;
    if (updates.city !== undefined) m.city = updates.city;
    if (updates.state !== undefined) m.state = updates.state;
    if (updates.score !== undefined) m.score = updates.score;
    if (updates.status !== undefined) m.status = updates.status;
    if (updates.ai !== undefined) m.ai_summary = updates.ai;
    if (updates.owner !== undefined) m.owner_id = updates.owner;
    if (updates.tags !== undefined) m.tags = updates.tags;
    // Collect custom field values into properties
    Object.keys(updates).forEach(k => {
      if (!knownKeys.has(k) && updates[k] !== undefined) {
        customProps[k] = updates[k];
      }
    });
    if (updates.properties !== undefined) {
      m.properties = { ...updates.properties, ...customProps };
    } else if (Object.keys(customProps).length > 0) {
      // Merge custom props with existing properties from the contact
      const existing = contactsRef.current.find(c => c.id === id)?.properties || {};
      m.properties = { ...existing, ...customProps };
    }
    if (Object.keys(m).length > 0) {
      // Build audit diff
      const existing = contactsRef.current.find(c => c.id === id);
      const diff = {};
      if (m.first_name !== undefined && m.first_name !== existing?.fn) diff.first_name = { old: existing?.fn, new: m.first_name };
      if (m.last_name !== undefined && m.last_name !== existing?.ln) diff.last_name = { old: existing?.ln, new: m.last_name };
      if (m.email !== undefined && m.email !== existing?.email) diff.email = { old: existing?.email, new: m.email };
      if (m.phone !== undefined && m.phone !== existing?.ph) diff.phone = { old: existing?.ph, new: m.phone };
      if (m.company !== undefined && m.company !== existing?.co) diff.company = { old: existing?.co, new: m.company };
      if (m.job_title !== undefined && m.job_title !== existing?.title) diff.job_title = { old: existing?.title, new: m.job_title };
      if (m.lead_stage !== undefined && m.lead_stage !== existing?.leadStage) diff.lead_stage = { old: existing?.leadStage, new: m.lead_stage };
      if (m.lead_source !== undefined && m.lead_source !== existing?.source) diff.lead_source = { old: existing?.source, new: m.lead_source };
      if (m.score !== undefined && m.score !== existing?.score) diff.score = { old: existing?.score, new: m.score };
      if (m.status !== undefined && m.status !== existing?.status) diff.status = { old: existing?.status, new: m.status };
      if (m.owner_id !== undefined) diff.owner = { old: existing?.ownerId, new: m.owner_id };

      const { error } = await supabase.from("contacts").update(m).eq("id", id);
      if (error) { console.error("[CF] Contact update err:", error.message); toast?.error("Failed to save contact changes"); return false; }
      
      // Log audit if there were actual changes
      if (Object.keys(diff).length > 0) {
        const name = (existing?.fn || "") + " " + (existing?.ln || "");
        logAudit("contact", id, name.trim(), "update", diff);
      }
    }
    return true;
  }, [dbReady]);

  const dbUpdateDeal = useCallback(async (id, updates) => {
    if (!dbReady) { console.warn("[CoreFlow] dbUpdateDeal skipped: dbReady=false"); return; }
    const m = {};
    // NOTE: If a future caller passes { ct: ... } here, the deal_contacts
    // junction will silently drift from the deals.contact_id mirror.
    // Prefer store.setPrimaryContact() for programmatic primary changes
    // so the junction stays in sync. See migration 046.
    const knownDealKeys = new Set(["name","val","stage","prob","days","ai","close","properties","ct","ownerId"]);
    const customProps = {};
    if (updates.name !== undefined) m.name = updates.name;
    if (updates.val !== undefined) m.value = updates.val;
    if (updates.stage !== undefined) m.stage = updates.stage;
    if (updates.prob !== undefined) m.probability = updates.prob;
    if (updates.days !== undefined) m.days_in_stage = updates.days;
    if (updates.ai !== undefined) m.ai_summary = updates.ai;
    if (updates.close !== undefined) m.close_date = updates.close || null;
    // Collect custom field values into properties
    Object.keys(updates).forEach(k => {
      if (!knownDealKeys.has(k) && updates[k] !== undefined) {
        customProps[k] = updates[k];
      }
    });
    if (updates.properties !== undefined) {
      m.properties = { ...updates.properties, ...customProps };
    } else if (Object.keys(customProps).length > 0) {
      const existing = dealsRef.current.find(d => d.id === id)?.properties || {};
      m.properties = { ...existing, ...customProps };
    }
    if (Object.keys(m).length > 0) {
      // Build audit diff
      const existing = dealsRef.current.find(d => d.id === id);
      const diff = {};
      if (m.name !== undefined && m.name !== existing?.name) diff.name = { old: existing?.name, new: m.name };
      if (m.value !== undefined && m.value !== existing?.val) diff.value = { old: existing?.val, new: m.value };
      if (m.stage !== undefined && m.stage !== existing?.stage) diff.stage = { old: existing?.stage, new: m.stage };
      if (m.probability !== undefined && m.probability !== existing?.prob) diff.probability = { old: existing?.prob, new: m.probability };
      if (m.close_date !== undefined && m.close_date !== existing?.close) diff.close_date = { old: existing?.close, new: m.close_date };
      // risk and source go through customProps into properties JSONB (not top-level
      // columns), but they're still auditable changes — track them here.
      if (updates.risk !== undefined && updates.risk !== existing?.risk) diff.risk = { old: existing?.risk, new: updates.risk };
      if (updates.source !== undefined && updates.source !== existing?.source) diff.source = { old: existing?.source, new: updates.source };

      console.log("[CoreFlow] Updating deal", id, m);
      const { error } = await supabase.from("deals").update(m).eq("id", id);
      if (error) { console.error("[CoreFlow] Deal update err:", error.message); toast?.error("Failed to save deal changes"); return false; }
      else console.log("[CoreFlow] Deal updated successfully");

      // Log audit if there were actual changes
      if (Object.keys(diff).length > 0) {
        logAudit("deal", id, existing?.name || "", "update", diff);
      }
    }
    return true;
  }, [dbReady]);

  const dbInsertActivity = useCallback(async (a) => {
    if (!dbReady) return null;
    const cid = (a.contactId && !String(a.contactId).startsWith("contact-")) ? a.contactId : null;
    const did = (a.dealId && !String(a.dealId).startsWith("deal-")) ? a.dealId : null;
    const coid = a.companyId || null;
    const row = {
      org_id: getOrgId(), type: a.type, title: a.title,
      notes: a.notes || null, contact_id: cid,
      deal_id: did, company_id: coid, duration: a.duration || null,
      user_id: a.user_id || currentUserId || null,
    };
    // Use caller-provided id when present so optimistic local state matches
    // the DB row's primary key — needed for the inline-edit feature.
    if (a.id) row.id = a.id;
    const { data, error } = await supabase.from("activities").insert(row).select().single();
    if (error) { console.warn("[CoreFlow] dbInsertActivity err:", error.message); return null; }
    return data;
  }, [dbReady, currentUserId]);

  const dbInsertTask = useCallback(async (t) => {
    if (!dbReady) return null;
    const cid = (t.ct && !String(t.ct).startsWith("contact-")) ? t.ct : null;
    const did = (t.deal && !String(t.deal).startsWith("deal-")) ? t.deal : null;
    const row = {
      org_id: getOrgId(),
      title: t.title,
      description: t.description || null,
      priority: t.pri || "medium",
      status: t.status || "pending",
      task_type: t.taskType || null,
      due_date: t.due || null,
      contact_id: cid,
      deal_id: did,
      assigned_to: t.assignee || null, // MUST be a UUID, not a name
      created_by: window.__coreflow_user_id || null,
    };
    const { data, error } = await supabase.from("tasks").insert(row).select().single();
    if (error) { console.error("[CoreFlow] dbInsertTask failed:", error); return null; }
    return data?.id || null;
  }, [dbReady]);

  const dbToggleTask = useCallback(async (id, done) => {
    if (!dbReady) return;
    await supabase.from("tasks").update({
      status: done ? "done" : "pending",
      completed_at: done ? new Date().toISOString() : null,
    }).eq("id", id);
  }, [dbReady]);

  // ── DB Insert Contact ──
  const dbInsertContact = useCallback(async (c) => {
    if (!dbReady) return null;
    try {
      const { data, error } = await supabase.from("contacts").insert({
        org_id: getOrgId(),
        owner_id: c.ownerId || currentUserId || null,
        first_name: c.fn, last_name: c.ln,
        email: c.email || null, phone: c.ph || c.phone || null,
        company: c.company || c.co || null, job_title: c.title || null,
        company_id: c.company_id || null,
        lead_source: c.source || "Inbound", lead_stage: c.leadStage || "new",
        account_type: c.type || "SMB", industry: c.industry || null,
        city: c.city || null, state: c.state || null,
        score: c.score || 50, status: c.status || "active",
        tags: c.tags || [], sentiment: c.sent || "neutral",
        ai_summary: c.ai || null, properties: c.properties || {},
      }).select().single();
      if (error) { console.warn("[CoreFlow] Insert contact failed:", error.message); toast?.error("Failed to create contact"); return null; }
      console.log("[CoreFlow] Contact saved to DB:", data.id);
      // Write a contact_created activity unless the caller suppressed
      // it (CSV imports do, since they emit one batch 'import' activity
      // instead of N per-row 'contact_created' rows). DB write +
      // optimistic local state push so the My Activity panel reflects
      // the new row without waiting for next page reload.
      if (!c._skipActivity) {
        try {
          const title = `Contact created: ${data.first_name || ""} ${data.last_name || ""}`.trim();
          const { data: actRow } = await supabase.from("activities").insert({
            org_id: data.org_id,
            user_id: currentUserId || null,
            contact_id: data.id,
            type: "contact_created",
            title,
          }).select().single();
          if (actRow) {
            setActivities(prev => [{
              id: actRow.id,
              type: "contact_created",
              title,
              notes: "",
              date: cfDateTime(actRow.created_at),
              created_at: actRow.created_at,
              contactId: data.id,
              dealId: "",
              companyId: null,
              by: currentUserId,
              duration: "",
              properties: {},
            }, ...prev]);
          }
        } catch (e) {
          console.warn("[CoreFlow] contact_created activity write failed:", e?.message);
        }
      }
      return data;
    } catch (e) { console.warn("[CoreFlow] Insert contact error:", e.message); toast?.error("Failed to create contact"); return null; }
  }, [dbReady, currentUserId]);

  // ── DB Insert Deal ──
  const dbInsertDeal = useCallback(async (d) => {
    if (!dbReady) return null;
    try {
      const { data, error } = await supabase.from("deals").insert({
        org_id: getOrgId(),
        owner_id: d.ownerId || currentUserId || null,
        contact_id: (d.ct && !String(d.ct).startsWith("contact-")) ? d.ct : null,
        name: d.name, value: d.val || 0,
        stage: d.stage || "Discovery", probability: d.prob || 20,
        days_in_stage: d.days || 0,
        close_date: d.close && d.close !== "TBD" ? d.close : null,
        ai_summary: d.ai || null, properties: d.properties || {},
      }).select().single();
      if (error) { console.warn("[CoreFlow] Insert deal failed:", error.message); toast?.error("Failed to create deal"); return null; }
      console.log("[CoreFlow] Deal saved to DB:", data.id);
      return data;
    } catch (e) { console.warn("[CoreFlow] Insert deal error:", e.message); toast?.error("Failed to create deal"); return null; }
  }, [dbReady]);

  // ── DB Delete Contact ──
  const dbDeleteContact = useCallback(async (id) => {
    if (!dbReady) return false;
    try {
      const { error } = await supabase.from("contacts").delete().eq("id", id);
      if (error) { console.warn("[CoreFlow] Delete contact error:", error.message); toast?.error("Failed to delete contact — it has been restored"); return false; }
      console.log("[CoreFlow] Contact deleted from DB:", id);
      return true;
    } catch (e) { console.warn("[CoreFlow] Delete contact error:", e.message); toast?.error("Failed to delete contact — it has been restored"); return false; }
  }, [dbReady]);

  // ── DB Delete Deal ──
  const dbDeleteDeal = useCallback(async (id) => {
    if (!dbReady) return false;
    try {
      const { error } = await supabase.from("deals").delete().eq("id", id);
      if (error) { console.warn("[CoreFlow] Delete deal error:", error.message); toast?.error("Failed to delete deal — it has been restored"); return false; }
      console.log("[CoreFlow] Deal deleted from DB:", id);
      return true;
    } catch (e) { console.warn("[CoreFlow] Delete deal error:", e.message); toast?.error("Failed to delete deal — it has been restored"); return false; }
  }, [dbReady]);

  // ── DB Delete Task ──
  const dbDeleteTask = useCallback(async (id) => {
    if (!dbReady) return false;
    try {
      const { error } = await supabase.from("tasks").delete().eq("id", id);
      if (error) { console.warn("[CoreFlow] Delete task error:", error.message); toast?.error("Failed to delete task — it has been restored"); return false; }
      return true;
    } catch (e) { console.warn("[CoreFlow] Delete task error:", e.message); toast?.error("Failed to delete task — it has been restored"); return false; }
  }, [dbReady]);

  // ── DB Update Company ──
  // Mirrors dbUpdateContact. Maps UI field names to DB columns; unknown
  // keys fold into the properties JSONB. Logs audit diff on changes.
  const dbUpdateCompany = useCallback(async (id, updates) => {
    if (!dbReady) return false;
    const m = {};
    const directColumns = new Set([
      "name","domain","industry","size","annual_revenue",
      "city","state","country","phone","description",
      "ai_summary","owner_id","tags","properties",
    ]);
    // Accept both DB column names and the UI aliases used by dbCompanyToLocal.
    const aliasMap = { annualRevenue: "annual_revenue", ai: "ai_summary", ownerId: "owner_id", owner: "owner_id" };
    const customProps = {};
    for (const [rawKey, value] of Object.entries(updates)) {
      if (value === undefined) continue;
      const key = aliasMap[rawKey] || rawKey;
      if (directColumns.has(key)) m[key] = value;
      else customProps[rawKey] = value;
    }
    if (updates.properties !== undefined) {
      m.properties = { ...updates.properties, ...customProps };
    } else if (Object.keys(customProps).length > 0) {
      const existing = companiesRef.current.find(c => c.id === id)?.properties || {};
      m.properties = { ...existing, ...customProps };
    }
    if (Object.keys(m).length === 0) return true;

    const existing = companiesRef.current.find(c => c.id === id);
    const diff = {};
    if (m.name !== undefined && m.name !== existing?.name) diff.name = { old: existing?.name, new: m.name };
    if (m.industry !== undefined && m.industry !== existing?.industry) diff.industry = { old: existing?.industry, new: m.industry };
    if (m.domain !== undefined && m.domain !== existing?.domain) diff.domain = { old: existing?.domain, new: m.domain };
    if (m.city !== undefined && m.city !== existing?.city) diff.city = { old: existing?.city, new: m.city };
    if (m.state !== undefined && m.state !== existing?.state) diff.state = { old: existing?.state, new: m.state };
    if (m.phone !== undefined && m.phone !== existing?.phone) diff.phone = { old: existing?.phone, new: m.phone };
    if (m.owner_id !== undefined && m.owner_id !== existing?.ownerId) diff.owner = { old: existing?.ownerId, new: m.owner_id };

    const { error } = await supabase.from("companies").update(m).eq("id", id);
    if (error) { console.warn("[CoreFlow] Company update err:", error.message); toast?.error("Failed to save company changes"); return false; }

    if (Object.keys(diff).length > 0) {
      logAudit("company", id, existing?.name || "", "update", diff);
    }
    return true;
  }, [dbReady]);

  // ── DB Delete Company ──
  const dbDeleteCompany = useCallback(async (id) => {
    if (!dbReady) return false;
    try {
      const { error } = await supabase.from("companies").delete().eq("id", id);
      if (error) { console.warn("[CoreFlow] Delete company error:", error.message); toast?.error("Failed to delete company — it has been restored"); return false; }
      return true;
    } catch (e) { console.warn("[CoreFlow] Delete company error:", e.message); toast?.error("Failed to delete company — it has been restored"); return false; }
  }, [dbReady]);

  // ── findOrCreateCompany ──
  // Resolves a company name to a company_id. Case-insensitive lookup against
  // the in-memory companies array (companiesRef stays in sync via setCompanies).
  // If found, returns existing id. If not, INSERTs into companies, updates
  // local state, returns new id. Returns null for empty/whitespace-only names
  // or when the insert fails — callers persist contact.company text either way.
  //
  // Used by AddContactModal (manual single-contact create). Bulk-import flows
  // should prefer findOrCreateCompaniesBulk to avoid N round-trips.
  const findOrCreateCompany = useCallback(async (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return null;
    const normalized = trimmed.toLowerCase();

    const existing = (companiesRef.current || []).find(
      c => (c.name || "").trim().toLowerCase() === normalized
    );
    if (existing) return existing.id;

    if (!dbReady) return null;
    const { data, error } = await supabase.from("companies").insert({
      org_id: getOrgId(),
      name: trimmed,
    }).select().single();
    if (error || !data) {
      console.warn("[CoreFlow] findOrCreateCompany insert failed:", error?.message);
      return null;
    }
    setCompanies(prev => [dbCompanyToLocal(data), ...prev]);
    return data.id;
  }, [dbReady]);

  // ── findOrCreateCompaniesBulk ──
  // Pre-pass for bulk imports. Takes an array of raw company name strings,
  // returns a map { normalized_name: companyId } that callers use to look up
  // company_id per row. Performs ONE batch INSERT for any names not already
  // in local state — N rows of CSV → 1 round-trip for company creation.
  //
  // Case-insensitive dedup via LOWER(TRIM(name)). First-seen casing wins.
  // Failed batch insert → those names get omitted from the result map (callers
  // fall back to leaving company_id null but preserving contact.company text).
  const findOrCreateCompaniesBulk = useCallback(async (names) => {
    const result = {};
    if (!Array.isArray(names) || names.length === 0) return result;

    // Normalize + dedupe input. Map preserves insertion order; first-seen
    // casing wins (matches migration 057's "earliest contact's spelling").
    const uniqueNames = new Map();
    for (const n of names) {
      const trimmed = (n || "").trim();
      if (!trimmed) continue;
      const normalized = trimmed.toLowerCase();
      if (!uniqueNames.has(normalized)) {
        uniqueNames.set(normalized, trimmed);
      }
    }
    if (uniqueNames.size === 0) return result;

    // Match against in-memory state.
    const liveCompanies = companiesRef.current || [];
    const toCreate = [];
    for (const [normalized, originalCasing] of uniqueNames) {
      const existing = liveCompanies.find(
        c => (c.name || "").trim().toLowerCase() === normalized
      );
      if (existing) {
        result[normalized] = existing.id;
      } else {
        toCreate.push({ org_id: getOrgId(), name: originalCasing, _normalized: normalized });
      }
    }

    if (toCreate.length === 0) return result;
    if (!dbReady) return result;

    // Batch insert. Strip _normalized before sending to Supabase.
    const insertRows = toCreate.map(({ _normalized, ...row }) => row);
    const { data, error } = await supabase.from("companies").insert(insertRows).select();
    if (error || !data) {
      console.error("[CoreFlow] findOrCreateCompaniesBulk batch insert failed:", error?.message);
      return result; // unfilled normalized names stay absent → callers see company_id = null
    }

    // Map returned rows back to normalized keys. Supabase preserves insert
    // order in the returned array, so we can pair by index.
    data.forEach((row, i) => {
      result[toCreate[i]._normalized] = row.id;
    });

    // Optimistic local-state update with the canonical adapter shape.
    setCompanies(prev => [...data.map(dbCompanyToLocal), ...prev]);
    return result;
  }, [dbReady]);

  // ═══════════════════════════════════════════
  // CRUD (same interface + DB sync)
  // ═══════════════════════════════════════════
  const updateContact = useCallback(async (id, updates) => {
    const oldContact = contactsRef.current.find(c => c.id === id);
    const snapshot = oldContact ? { ...oldContact } : null;
    setContacts(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
    const ok = await dbUpdateContact(id, updates);
    if (!ok && snapshot) {
      setContacts(prev => prev.map(c => c.id === id ? snapshot : c));
      return;
    }
    if (ok) showSaved();
    // Fire workflow triggers for property/score changes
    if (ok && snapshot) {
      const newContact = { ...snapshot, ...updates };
      if (updates.leadStage || updates.status || updates.score !== undefined || updates.industry || updates.owner) {
        checkAndFireWorkflows("contact_property", { contactId: id, contact: newContact, oldContact: snapshot, triggerReason: "Contact property changed" }).catch(() => {});
      }
      if (updates.score !== undefined && updates.score !== snapshot.score) {
        checkAndFireWorkflows("score_threshold", { contactId: id, contact: newContact, score: updates.score, triggerReason: `Score changed to ${updates.score}` }).catch(() => {});
      }
      if (updates.tags && !snapshot.tags?.length && updates.tags.length) {
        checkAndFireWorkflows("tag_added", { contactId: id, contact: newContact, tag: updates.tags[updates.tags.length - 1], triggerReason: "Tag added" }).catch(() => {});
      }
    }
  }, [dbUpdateContact, showSaved]);

  const getContact = useCallback((id) => contacts.find(c => c.id === id), [contacts]);

  const updateDeal = useCallback(async (id, updates) => {
    // Capture old values from state BEFORE the optimistic update so we can
    // emit accurate lifecycle entries after the DB write succeeds.
    const prevDeal = dealsRef.current.find(d => d.id === id);
    const oldVal = prevDeal?.val;
    const oldOwner = prevDeal?.ownerId || prevDeal?.owner;
    const prevContactId = prevDeal?.ct;

    setDeals(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
    const ok = await dbUpdateDeal(id, updates);
    if (!ok) return;
    showSaved();

    // Visible timeline entries — only for the two high-signal changes.
    if (updates.val !== undefined && Number(updates.val) !== Number(oldVal)) {
      addActivity({
        type: "lifecycle",
        contactId: prevContactId,
        dealId: id,
        title: "Amount updated",
        notes: (oldVal || oldVal === 0 ? fmtDollar(oldVal) : "—") + " → " + fmtDollar(updates.val),
      });
    }
    if (updates.owner !== undefined && updates.owner !== oldOwner) {
      addActivity({
        type: "lifecycle",
        contactId: prevContactId,
        dealId: id,
        title: "Owner changed",
        notes: (getOwnerName(oldOwner) || "Unassigned") + " → " + (getOwnerName(updates.owner) || "Unassigned"),
      });
    }
  }, [dbUpdateDeal, showSaved]);

  const updateDealStage = useCallback(async (dealId, newStage, stageNote) => {
    const deal = deals.find(d => d.id === dealId);
    const oldStage = deal?.stage;
    
    // Check stage gate before allowing move
    if (governanceSettings?.enforceStageGates) {
      try {
        const resp = await apiFetch("/api/audit", {
          method: "POST",
          body: JSON.stringify({ action: "check_stage_gate", dealId, fromStage: oldStage, toStage: newStage, orgId: window.__coreflow_org_id }),
        });
        const gate = await resp.json();
        if (gate.errors?.length > 0) {
          toast?.error(gate.errors[0]);
          return false;
        }
        if (gate.requireNote && !stageNote) {
          // Signal to UI that a note is required
          window.__coreflow_stage_gate_note = { dealId, newStage };
          toast?.error("A note is required when moving to " + newStage);
          return "require_note";
        }
      } catch (e) { /* allow move if gate check fails */ }
    }

    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, stage: newStage, prob: newStage === "Closed Won" ? 100 : d.prob } : d));
    const ok = await dbUpdateDeal(dealId, { stage: newStage });
    if (!ok && oldStage) {
      setDeals(prev => prev.map(d => d.id === dealId ? { ...d, stage: oldStage } : d));
      return;
    }
    if (ok) showSaved();
    
    // Log stage change with note
    const actTitle = stageNote 
      ? `Deal moved to ${newStage}: ${stageNote}`
      : `Deal moved to ${newStage}`;
    if (deal) addActivity({ type: "lifecycle", contactId: deal.ct, dealId, title: actTitle, notes: (deal.name || "") + " -> " + newStage + (stageNote ? " | " + stageNote : "") });
    
    // Audit log the stage change
    logAudit("deal", dealId, deal?.name || "", "stage_change", null, "stage", oldStage, newStage);

    // Fire workflow triggers
    if (ok && deal) {
      const contact = contacts.find(c => c.id === deal.ct);
      const ctx = { contactId: deal.ct, contact, deal, dealId, dealName: deal.name, dealValue: deal.val, fromStage: oldStage, toStage: newStage, triggerReason: `Deal moved from ${oldStage} to ${newStage}` };
      checkAndFireWorkflows("deal_stage", ctx).catch(() => {});
      if (newStage === "Closed Won") checkAndFireWorkflows("deal_won", ctx).catch(() => {});
      if (newStage === "Closed Lost") checkAndFireWorkflows("deal_lost", ctx).catch(() => {});
    }
  }, [deals, dbUpdateDeal, showSaved, contacts, governanceSettings]);

  const getDealsForContact = useCallback((cid) => deals.filter(d => d.ct === cid), [deals]);

  // ── Create Contact (state + DB) ──
  const createContact = useCallback(async (contactData) => {
    // Add to state immediately with temp ID
    const tempId = "contact-" + Date.now();
    const newContact = {
      id: tempId, score: 50, sent: "neutral", status: "active",
      activities: [], tags: [], properties: {}, ai: "",
      type: "SMB", industry: "", city: "", state: "", leadStage: "new",
      lastActivity: cfDate(new Date()),
      created: cfDate(new Date()),
      last: "Just now", owner: currentUser,
      ...contactData,
    };
    setContacts(prev => [newContact, ...prev]);

    // Write to DB and update with real ID
    const dbRow = await dbInsertContact(newContact);
    if (dbRow) {
      const realContact = dbContactToLocal(dbRow);
      realContact.activities = [];
      setContacts(prev => prev.map(c => c.id === tempId ? realContact : c));
      // Fire workflow triggers
      checkAndFireWorkflows("contact_added", { contactId: realContact.id, contact: realContact, source: contactData.source || "Manual", triggerReason: "New contact created" }).catch(() => {});
      return realContact;
    }
    return newContact;
  }, [currentUser, dbInsertContact]);

  // ── Deal ↔ Contact junction methods (see migration 046) ──

  // Atomic demote-old + promote-new + sync deals.contact_id mirror via RPC.
  const setPrimaryContact = useCallback(async (dealId, contactId) => {
    if (!dbReady) return { ok: false, error: "not_ready" };
    const { error } = await supabase.rpc("set_deal_primary_contact", {
      p_deal_id: dealId, p_contact_id: contactId,
    });
    if (error) {
      console.warn("[CoreFlow] setPrimaryContact failed:", error.message);
      toast?.error("Failed to set primary contact");
      return { ok: false, error: error.message };
    }
    setDealContacts(prev => prev.map(dc =>
      dc.deal_id === dealId
        ? { ...dc, is_primary: dc.contact_id === contactId }
        : dc
    ));
    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, ct: contactId } : d));
    return { ok: true };
  }, [dbReady, toast]);

  // Insert (or upsert-if-already-present) a junction row. If isPrimary,
  // delegate to setPrimaryContact for atomic demote/promote + mirror sync.
  const addContactToDeal = useCallback(async (dealId, contactId, { isPrimary = false } = {}) => {
    if (!dbReady) return { ok: false, error: "not_ready" };
    const { data, error } = await supabase.from("deal_contacts").insert({
      org_id: getOrgId(),
      deal_id: dealId,
      contact_id: contactId,
      is_primary: false,
      added_by: currentUserId || null,
    }).select().single();
    if (error && error.code !== "23505") {  // 23505 = unique_violation, already present
      console.warn("[CoreFlow] addContactToDeal insert failed:", error.message);
      toast?.error("Failed to add contact to deal");
      return { ok: false, error: error.message };
    }
    if (data) {
      setDealContacts(prev => {
        const filtered = prev.filter(dc => !(dc.deal_id === dealId && dc.contact_id === contactId));
        return [...filtered, data];
      });
    }
    if (isPrimary) {
      return await setPrimaryContact(dealId, contactId);
    }
    return { ok: true };
  }, [dbReady, currentUserId, toast, setPrimaryContact]);

  // Delete a junction row. If the target is primary AND other contacts exist,
  // block with a toast. If primary and no others, allow — migration 047's DB
  // trigger nulls deals.contact_id on primary deletion. Non-primary: delete.
  const removeContactFromDeal = useCallback(async (dealId, contactId) => {
    if (!dbReady) return { ok: false, error: "not_ready" };
    const dealRows = dealContacts.filter(dc => dc.deal_id === dealId);
    const target = dealRows.find(dc => dc.contact_id === contactId);
    if (!target) return { ok: false, error: "not_found" };
    if (target.is_primary) {
      const others = dealRows.filter(dc => dc.contact_id !== contactId);
      if (others.length > 0) {
        toast?.info("Promote another contact to primary first.");
        return { ok: false, error: "primary_needs_replacement" };
      }
      // Falls through: last contact is allowed to go; trigger nulls mirror.
    }
    const { error } = await supabase.from("deal_contacts").delete()
      .eq("deal_id", dealId).eq("contact_id", contactId);
    if (error) {
      console.warn("[CoreFlow] removeContactFromDeal failed:", error.message);
      toast?.error("Failed to remove contact");
      return { ok: false, error: error.message };
    }
    setDealContacts(prev => prev.filter(dc => !(dc.deal_id === dealId && dc.contact_id === contactId)));
    if (target.is_primary) {
      // DB trigger (migration 047) nulls deals.contact_id server-side.
      // Mirror the same change in local state so the UI matches.
      setDeals(prev => prev.map(d => d.id === dealId ? { ...d, ct: null } : d));
    }
    return { ok: true };
  }, [dbReady, dealContacts, toast]);

  // Memoized selector. Returns enriched rows, primary first.
  const getContactsForDeal = useCallback((dealId) => {
    return dealContacts
      .filter(dc => dc.deal_id === dealId)
      .map(dc => ({
        contact: contacts.find(c => c.id === dc.contact_id) || null,
        is_primary: dc.is_primary,
        created_at: dc.created_at,
        added_by: dc.added_by,
      }))
      .filter(row => row.contact)
      .sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));
  }, [dealContacts, contacts]);

  // ── Create Deal (state + DB) ──
  const createDeal = useCallback(async (dealData) => {
    const tempId = "deal-" + Date.now();
    const newDeal = {
      id: tempId, risk: "low", days: 0, close: "", ai: "",
      properties: {}, owner: "", co: "", prob: 20, stage: "Discovery",
      created: cfDate(new Date()),
      ...dealData,
    };
    setDeals(prev => [newDeal, ...prev]);

    // Write to DB and update with real ID
    const dbRow = await dbInsertDeal(newDeal);
    if (dbRow) {
      const realDeal = dbDealToLocal(dbRow);
      setDeals(prev => prev.map(d => d.id === tempId ? realDeal : d));
      // Persist the primary-contact association (see migration 046)
      if (realDeal.ct) {
        await addContactToDeal(realDeal.id, realDeal.ct, { isPrimary: true });
      }
      return realDeal;
    }
    return newDeal;
  }, [dbInsertDeal, addContactToDeal]);

  // ── Delete Contact (state + DB) ──
  const deleteContact = useCallback(async (id) => {
    const backup = contactsRef.current.find(c => c.id === id);
    setContacts(prev => prev.filter(c => c.id !== id));
    const ok = await dbDeleteContact(id);
    if (!ok && backup) setContacts(prev => [backup, ...prev]);
    else toast?.success("Contact deleted");
  }, [dbDeleteContact]);

  // ── Delete Deal (state + DB) ──
  const deleteDeal = useCallback(async (id) => {
    const backup = deals.find(d => d.id === id);
    setDeals(prev => prev.filter(d => d.id !== id));
    const ok = await dbDeleteDeal(id);
    if (!ok && backup) setDeals(prev => [backup, ...prev]);
    else toast?.success("Deal deleted");
  }, [deals, dbDeleteDeal]);

  // ── Bulk Delete Deals (state + DB, single aggregated toast) ──
  const bulkDeleteDeals = useCallback(async (ids) => {
    if (!ids || ids.length === 0) return;
    const backup = deals.filter(d => ids.includes(d.id));
    setDeals(prev => prev.filter(d => !ids.includes(d.id)));
    if (!dbReady) {
      toast?.success(ids.length === 1 ? "1 deal deleted" : `${ids.length} deals deleted`);
      return;
    }
    const { error } = await supabase.from("deals").delete().in("id", ids);
    if (error) {
      console.warn("[CoreFlow] Bulk delete deals error:", error.message);
      setDeals(prev => [...backup, ...prev]);
      toast?.error("Failed to delete deals — they have been restored");
      return;
    }
    toast?.success(ids.length === 1 ? "1 deal deleted" : `${ids.length} deals deleted`);
  }, [deals, dbReady]);

  // ── Update Company (optimistic, DB-synced, rollback on failure) ──
  const updateCompany = useCallback(async (id, updates) => {
    const snapshot = companiesRef.current.find(c => c.id === id);
    if (!snapshot) return;
    setCompanies(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
    const ok = await dbUpdateCompany(id, updates);
    if (!ok) {
      setCompanies(prev => prev.map(c => c.id === id ? snapshot : c));
      return;
    }
    showSaved();
  }, [dbUpdateCompany, showSaved]);

  // ── Delete Company (state + DB) ──
  const deleteCompany = useCallback(async (id) => {
    const backup = companiesRef.current.find(c => c.id === id);
    setCompanies(prev => prev.filter(c => c.id !== id));
    const ok = await dbDeleteCompany(id);
    if (!ok && backup) setCompanies(prev => [backup, ...prev]);
    else toast?.success("Company deleted");
  }, [dbDeleteCompany]);

  // ── Bulk Delete Companies (mirrors bulkDeleteDeals) ──
  const bulkDeleteCompanies = useCallback(async (ids) => {
    if (!ids || ids.length === 0) return;
    const backup = companiesRef.current.filter(c => ids.includes(c.id));
    setCompanies(prev => prev.filter(c => !ids.includes(c.id)));
    if (!dbReady) {
      toast?.success(ids.length === 1 ? "1 company deleted" : `${ids.length} companies deleted`);
      return;
    }
    const { error } = await supabase.from("companies").delete().in("id", ids);
    if (error) {
      console.warn("[CoreFlow] Bulk delete companies error:", error.message);
      setCompanies(prev => [...backup, ...prev]);
      toast?.error("Failed to delete companies — they have been restored");
      return;
    }
    toast?.success(ids.length === 1 ? "1 company deleted" : `${ids.length} companies deleted`);
  }, [dbReady]);

  // ── Email soft-delete / restore / hard-delete ──
  // EmailPage owns its own local `emails` state and calls loadEmails() after
  // these; the store does not mirror emails. Each returns true on success.
  const softDeleteEmails = useCallback(async (ids) => {
    if (!ids || ids.length === 0) return false;
    const { error } = await supabase.from("emails")
      .update({ deleted_at: new Date().toISOString() })
      .in("id", ids);
    if (error) {
      console.warn("[CoreFlow] softDeleteEmails error:", error.message);
      toast?.error("Failed to delete email" + (ids.length > 1 ? "s" : ""));
      return false;
    }
    return true;
  }, []);

  const restoreEmails = useCallback(async (ids) => {
    if (!ids || ids.length === 0) return false;
    const { error } = await supabase.from("emails")
      .update({ deleted_at: null })
      .in("id", ids);
    if (error) {
      console.warn("[CoreFlow] restoreEmails error:", error.message);
      toast?.error("Failed to restore email" + (ids.length > 1 ? "s" : ""));
      return false;
    }
    return true;
  }, []);

  const hardDeleteEmails = useCallback(async (ids) => {
    if (!ids || ids.length === 0) return false;
    const { error } = await supabase.from("emails")
      .delete()
      .in("id", ids);
    if (error) {
      console.warn("[CoreFlow] hardDeleteEmails error:", error.message);
      toast?.error("Failed to permanently delete email" + (ids.length > 1 ? "s" : ""));
      return false;
    }
    return true;
  }, []);

  // ── Trigger the manual sync-backfill on demand. EmailPage owns its own
  // `emails` state, so the caller is responsible for reloading after
  // this resolves. Returns the endpoint response: { ingested, fetched }.
  //
  // Hits /api/email/sync-backfill (Microsoft-only, last 7 days) rather
  // than the cron at /api/email/check-replies. The backfill endpoint
  // gives newly-connected accounts an immediate populated inbox without
  // waiting for the cron's rolling 15-min Pass 2 windows to accumulate.
  // Uses apiFetch so the user's Supabase JWT is attached as a Bearer
  // token — the endpoint identifies the calling user via verifyAuth.
  const syncEmails = useCallback(async () => {
    try {
      const r = await apiFetch('/api/email/sync-backfill', { method: 'POST' });
      if (!r.ok) throw new Error('sync failed (' + r.status + ')');
      return await r.json();
    } catch (e) {
      console.warn("[CoreFlow] syncEmails error:", e.message);
      toast?.error("Failed to sync emails: " + e.message);
      throw e;
    }
  }, []);

  // ── Per-user email read tracking. Idempotent upsert into email_reads.
  // EmailPage owns the local emails state and applies its own optimistic
  // update; this helper just persists the row.
  const markEmailRead = useCallback(async (emailId) => {
    if (!currentUserId || !emailId) return;
    try {
      await supabase.from("email_reads").upsert({
        email_id: emailId,
        user_id: currentUserId,
        read_at: new Date().toISOString(),
      }, { onConflict: "email_id,user_id" });
    } catch (e) {
      console.warn("[CoreFlow] markEmailRead error:", e.message);
    }
  }, [currentUserId]);

  // ── Inbox-access grants (admin-managed via AdminCenter Email Access tab) ──
  // Updates email_connections.shared_with for one connection. Refreshes local
  // state on success so consumers (EmailPage filter) react immediately.
  const updateConnectionSharedWith = useCallback(async (connectionId, sharedWithArray) => {
    const { error } = await supabase.from("email_connections")
      .update({ shared_with: sharedWithArray })
      .eq("id", connectionId);
    if (error) {
      console.warn("[CoreFlow] updateConnectionSharedWith error:", error.message);
      toast?.error("Failed to save inbox access");
      throw error;
    }
    setEmailConnections(prev => prev.map(c =>
      c.id === connectionId ? { ...c, shared_with: sharedWithArray } : c
    ));
  }, []);

  // ── Inbox-access read helper. Returns the set of users.id values whose
  // emails the current user is allowed to see. Always includes the current
  // user. Adds any other user whose connection has the current user listed
  // in shared_with. Skips connections with null user_id (schema allows it).
  // Note: store's `currentUser` is a display-name string; we use
  // `currentUserId` (= users.id UUID) which is what emails.user_id holds.
  const getAccessibleUserIds = useCallback(() => {
    if (!currentUserId) return [];
    const accessible = new Set([currentUserId]);
    for (const conn of emailConnections) {
      if (!conn.user_id) continue;
      const sw = Array.isArray(conn.shared_with) ? conn.shared_with : [];
      if (sw.includes(currentUserId)) accessible.add(conn.user_id);
    }
    return Array.from(accessible);
  }, [currentUserId, emailConnections]);

  // ── Delete Task (state + DB) ──
  const deleteTask = useCallback(async (id) => {
    const backup = tasks.find(t => t.id === id);
    setTasks(prev => prev.filter(t => t.id !== id));
    const ok = await dbDeleteTask(id);
    if (!ok && backup) setTasks(prev => [...prev, backup]);
    else toast?.success("Task deleted");
  }, [tasks, dbDeleteTask]);
  const getDealsForCompany = useCallback((co) => deals.filter(d => d.co === co), [deals]);

  const toggleTask = useCallback((id) => {
    const task = tasks.find(t => t.id === id);
    setTasks(prev => prev.map(t => { if (t.id === id) { dbToggleTask(id, !t.done); return { ...t, done: !t.done }; } return t; }));
    // Fire workflow trigger on task completion
    if (task && !task.done && task.ct) {
      const contact = contacts.find(c => c.id === task.ct);
      checkAndFireWorkflows("task_completed", { contactId: task.ct, contact, taskId: id, triggerReason: `Task completed: ${task.title}` }).catch(() => {});
    }
  }, [tasks, contacts, dbToggleTask]);

  const updateTask = useCallback(async (id, updates) => {
    // Capture old status before the optimistic update so we can set/clear
    // completed_at correctly when status transitions across "done".
    const prevTask = tasks.find(t => t.id === id);
    const oldStatus = prevTask?.status;

    setTasks(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
    if (dbReady && id && !String(id).startsWith("task-")) {
      const m = {};
      if (updates.title !== undefined) m.title = updates.title;
      if (updates.description !== undefined) m.description = updates.description;
      if (updates.pri !== undefined) m.priority = updates.pri;
      if (updates.status !== undefined) m.status = updates.status;
      if (updates.taskType !== undefined) m.task_type = updates.taskType;
      if (updates.due !== undefined) m.due_date = updates.due;
      if (updates.ct !== undefined) m.contact_id = (updates.ct && !String(updates.ct).startsWith("contact-")) ? updates.ct : null;
      if (updates.deal !== undefined) m.deal_id = (updates.deal && !String(updates.deal).startsWith("deal-")) ? updates.deal : null;
      if (updates.assignee !== undefined) m.assigned_to = updates.assignee || null; // UUID
      // completed_at transitions on status edges
      if (updates.status !== undefined && updates.status !== oldStatus) {
        if (updates.status === "done") m.completed_at = new Date().toISOString();
        else if (oldStatus === "done") m.completed_at = null;
      }
      if (Object.keys(m).length > 0) {
        const { error } = await supabase.from("tasks").update(m).eq("id", id);
        if (error) { console.error("[CoreFlow] Task update err:", error.message); toast?.error("Failed to save task changes"); }
        else showSaved();
      }
    }
  }, [dbReady, tasks]);

  const addTask = useCallback((task) => {
    const newId = "task-" + Date.now();
    const newTask = { id: newId, done: false, ...task };
    setTasks(prev => [...prev, newTask]);
    dbInsertTask(task);
    addActivity({ type: "task", contactId: task.ct, dealId: task.deal || task.dealId, title: "Task created: " + task.title, notes: "Priority: " + (task.pri || "medium") + " | Due: " + (task.due || "TBD") });
    return newTask;
  }, [tasks, dbInsertTask]);

  const getTasksForDeal = useCallback((did) => tasks.filter(t => t.deal === did), [tasks]);
  const getTasksForContact = useCallback((cid) => tasks.filter(t => t.ct === cid), [tasks]);

  // Optimistic insert. Uses a real UUID for the local id so it matches the
  // DB row that dbInsertActivity creates — this lets in-session edits work.
  // `by` is forced to currentUserId (UUID) regardless of caller input so the
  // shape matches DB-loaded rows. Returns the new activity (with reconciled
  // id from the DB) so callers like addNote/logCall/etc can fan it out into
  // contact.activities denormalized cache by reference.
  const addActivity = useCallback(async (activity) => {
    const now = new Date();
    const ts = cfDateTime(now);
    const tempId = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : "temp-" + Date.now();
    // Spread first so caller props provide title/notes/etc; then `by`,
    // `id`, and `created_at` override any caller-provided value. The
    // ISO created_at lets timezone-aware date bucketing in the My
    // Activity panel parse this row correctly without reverse-parsing
    // the formatted `date` string.
    const newActivity = { date: ts, ...activity, id: tempId, by: currentUserId, created_at: now.toISOString() };
    setActivities(prev => [newActivity, ...prev]);
    const dbResult = await dbInsertActivity({ ...activity, id: tempId, user_id: currentUserId });
    if (dbResult && dbResult.id && dbResult.id !== tempId) {
      // DB rejected our id (RLS / column default override). Reconcile the
      // local row's id so subsequent edits target the right DB row.
      setActivities(prev => prev.map(a => a.id === tempId ? { ...a, id: dbResult.id } : a));
      return { ...newActivity, id: dbResult.id };
    }
    return newActivity;
  }, [dbInsertActivity, currentUserId]);

  // Edit an existing activity (title/notes). Optimistic local update of both
  // store.activities and any contact.activities denormalized cache, then DB
  // write. Returns true on success, false on DB error.
  const updateActivity = useCallback(async (activityId, updates) => {
    if (!activityId) return false;
    setActivities(prev => prev.map(a => a.id === activityId ? { ...a, ...updates } : a));
    setContacts(prev => prev.map(c => {
      if (!c.activities) return c;
      const idx = c.activities.findIndex(a => a.id === activityId);
      if (idx === -1) return c;
      const newActs = [...c.activities];
      newActs[idx] = { ...newActs[idx], ...updates };
      return { ...c, activities: newActs };
    }));
    const { error } = await supabase.from("activities").update(updates).eq("id", activityId);
    if (error) {
      console.warn("[CoreFlow] updateActivity error:", error.message);
      return false;
    }
    return true;
  }, []);

  // Delete an activity. Optimistic local removal from both store.activities
  // and any contact.activities denormalized cache, then DB delete. Returns
  // true on success, false on DB error.
  const deleteActivity = useCallback(async (activityId) => {
    if (!activityId) return false;
    setActivities(prev => prev.filter(a => a.id !== activityId));
    setContacts(prev => prev.map(c => {
      if (!c.activities) return c;
      return { ...c, activities: c.activities.filter(a => a.id !== activityId) };
    }));
    const { error } = await supabase.from("activities").delete().eq("id", activityId);
    if (error) {
      console.warn("[CoreFlow] deleteActivity error:", error.message);
      return false;
    }
    return true;
  }, []);

  // The four wrappers below all share the same shape: persist via addActivity
  // and fan the resulting row into contact.activities denormalized cache by
  // reference, so the local id+by match the canonical row in store.activities.
  const addNote = useCallback(async (note) => {
    const newAct = await addActivity({ type: "note", ...note, title: note.title || "Note added" });
    if (note.contactId && newAct) {
      setContacts(prev => prev.map(c => c.id === note.contactId ? {
        ...c,
        activities: [newAct, ...(c.activities || [])],
        lastActivity: cfDate(new Date()),
      } : c));
    }
  }, [addActivity]);

  const logCall = useCallback(async ({ contactId, dealId, title, notes, duration, disposition }) => {
    const newAct = await addActivity({ type: "call", contactId, dealId, title: title || "Call", notes, duration });
    if (contactId && newAct) {
      setContacts(prev => prev.map(c => c.id === contactId ? {
        ...c,
        activities: [newAct, ...(c.activities || [])],
        last: "just now", lastActivity: cfDate(new Date()),
      } : c));
      // Fire workflow trigger for call completion
      const contact = contactsRef.current.find(c => c.id === contactId);
      if (contact) {
        checkAndFireWorkflows("call_completed", { contactId, contact, duration: duration || 0, outcome: disposition || title, dealId, triggerReason: `Call completed: ${disposition || title}` }).catch(() => {});
      }
    }
  }, [addActivity]);

  const logEmail = useCallback(async ({ contactId, dealId, title, notes }) => {
    const newAct = await addActivity({ type: "email", contactId, dealId, title: title || "Email sent", notes });
    if (contactId && newAct) {
      setContacts(prev => prev.map(c => c.id === contactId ? {
        ...c,
        activities: [newAct, ...(c.activities || [])],
        last: "just now", lastActivity: cfDate(new Date()),
      } : c));
      // Fire workflow trigger for email sent
      const contact = contactsRef.current.find(c => c.id === contactId);
      if (contact) {
        checkAndFireWorkflows("email_sent", { contactId, contact, dealId, subject: title, triggerReason: `Email sent: ${title}` }).catch(() => {});
      }
    }
  }, [addActivity]);

  const logMeeting = useCallback(async ({ contactId, dealId, title, notes }) => {
    const newAct = await addActivity({ type: "meeting", contactId, dealId, title: title || "Meeting", notes });
    if (contactId && newAct) {
      setContacts(prev => prev.map(c => c.id === contactId ? {
        ...c,
        activities: [newAct, ...(c.activities || [])],
        last: "just now", lastActivity: cfDate(new Date()),
      } : c));
    }
  }, [addActivity]);

  const getActivitiesForContact = useCallback((cid) => { const c = contacts.find(c => c.id === cid); return c?.activities || []; }, [contacts]);
  const getActivitiesForDeal = useCallback((did) => activities.filter(a => a.dealId === did), [activities]);

  // ═══════════════════════════════════════════
  // CALL QUEUE: Supabase-backed per-user queue
  // ═══════════════════════════════════════════
  const setCallQueue = useCallback((updater) => {
    setCallQueueRaw(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return next;
    });
  }, []);

  // Returns { added, skipped } so callers can toast a "X added, Y skipped"
  // message. skipped = duplicates already in queue + contacts excluded by
  // disposition behavior (DNC, qualified, awaiting requeue). Existing callers
  // that ignore the return value continue to work.
  const addToQueue = useCallback(async (items) => {
    const userId = currentUserId || teamMembers.find(m => m._dbId)?._dbId;
    if (!userId) { console.warn("[CoreFlow] No userId for queue"); return { added: 0, skipped: 0 }; }

    // Filter out contacts already in queue
    const existingCts = new Set(callQueue.map(q => q.ct));
    const dedupItems = items.filter(item => !existingCts.has(item.ct));

    // Filter out contacts that are excluded by disposition behavior:
    //   do_not_call=true            → never callable from any UI surface
    //   qualified_at IS NOT NULL    → out of Power Dialer queue (still manually callable elsewhere)
    //   requeue_after > NOW()       → still waiting out the cool-down window
    // contactsRef gives us the latest contact state without rebinding the
    // useCallback every time contacts changes.
    const now = Date.now();
    const isEligible = (ctId) => {
      const c = contactsRef.current.find(x => x.id === ctId);
      if (!c) return true; // unknown contact — let DB decide / fall through
      if (c.do_not_call) return false;
      if (c.qualified_at) return false;
      if (c.requeue_after && new Date(c.requeue_after).getTime() > now) return false;
      return true;
    };
    const newItems = dedupItems.filter(item => isEligible(item.ct));
    const skipped = items.length - newItems.length;

    if (newItems.length === 0) {
      if (skipped > 0) console.log("[CoreFlow] addToQueue: 0 added, " + skipped + " skipped (duplicates / DNC / qualified / awaiting requeue)");
      return { added: 0, skipped };
    }

    const rows = newItems.map((item, i) => ({
      org_id: getOrgId(),
      user_id: userId,
      contact_id: item.ct,
      priority: item.pri || "warm",
      reason: item.reason || "",
      attempts: item.attempts || 0,
      best_time: item.best || "",
      position: i,
      status: "pending",
    }));

    try {
      const { data, error } = await supabase.from("call_queue_items").insert(rows).select();
      if (error) {
        console.error("[CoreFlow] Queue insert error:", error.message); toast?.error("Failed to add to call queue");
        return { added: 0, skipped };
      }
      if (data) {
        const local = data.map(r => ({
          id: r.id, ct: r.contact_id, pri: r.priority, reason: r.reason,
          attempts: r.attempts, best: r.best_time, _dbId: r.id,
        }));
        setCallQueueRaw(prev => [...prev, ...local]);
        console.log("[CoreFlow] ✅ Added", data.length, "to queue (" + skipped + " skipped)");
        return { added: data.length, skipped };
      }
      return { added: 0, skipped };
    } catch (e) {
      console.error("[CoreFlow] Queue save failed:", e.message); toast?.error("Failed to save call queue");
      return { added: 0, skipped };
    }
  }, [teamMembers, currentUserId, callQueue]);

  const removeFromQueue = useCallback(async (id) => {
    setCallQueueRaw(prev => {
      const item = prev.find(q => q.id === id);
      if (item?._dbId) {
        supabase.from("call_queue_items").delete().eq("id", item._dbId).then(() => {});
      }
      return prev.filter(q => q.id !== id);
    });
  }, []);

  const clearQueue = useCallback(async () => {
    // Prefer currentUserId; fall back to first team member with _dbId only
    // if the auth-derived id isn't ready yet. Matches addToQueue's pattern.
    // The old find()-only path picked an arbitrary user in multi-user orgs.
    const userId = currentUserId || teamMembers.find(m => m._dbId)?._dbId;
    if (userId) {
      await supabase.from("call_queue_items").delete().eq("user_id", userId).eq("status", "pending");
    }
    setCallQueueRaw([]);
  }, [teamMembers, currentUserId]);

  // ═══════════════════════════════════════════
  // SAVED VIEWS: Supabase-backed per-user views
  // ═══════════════════════════════════════════
  const setSavedViews = useCallback((updater) => {
    setSavedViewsRaw(prev => typeof updater === "function" ? updater(prev) : updater);
  }, []);

  const saveView = useCallback(async (view) => {
    const userId = currentUserId || teamMembers.find(m => m._dbId)?._dbId;
    if (!userId) { console.warn("[CoreFlow] No userId for view save"); return null; }

    const row = {
      org_id: getOrgId(),
      user_id: userId,
      name: view.name,
      icon: view.icon || null,
      filters: view.filters || [],
      entity: view.entity || "contacts",
      is_shared: view.isShared || false,
      position: 0,
    };

    try {
      const { data, error } = await supabase.from("saved_views").insert(row).select().single();
      if (error) {
        console.error("[CoreFlow] View save error:", error.message);
        return null;
      }
      if (data) {
        const local = { id: data.id, name: data.name, icon: data.icon, filters: data.filters, entity: data.entity, isShared: data.is_shared, _dbId: data.id };
        setSavedViewsRaw(prev => [...prev, local]);
        console.log("[CoreFlow] ✅ View saved:", data.id);
        return local;
      }
    } catch (e) { console.error("[CoreFlow] View save failed:", e.message); }
    return null;
  }, [teamMembers, currentUserId]);

  const deleteView = useCallback(async (viewId) => {
    setSavedViewsRaw(prev => {
      const item = prev.find(v => v.id === viewId);
      if (item?._dbId) {
        supabase.from("saved_views").delete().eq("id", item._dbId).then(() => {});
      }
      return prev.filter(v => v.id !== viewId);
    });
  }, []);

  const resetData = useCallback(() => {
    setContacts([]); setDeals([]); setTasks([]); setCompanies([]);
    setEmailConnections([]);
    setCallQueueRaw([]); setActivities([]); setTeamMembers([]);
    setPipelineStages(SEED_PIPELINE); setLeadStages(SEED_LEAD_STAGES);
    setCustomPipelines([]); setCustomPipelineItems([]);
    setCustomFields({ contacts: [], deals: [], companies: [], tasks: [], calls: [], emails: [], sms: [], calendar: [] });
    setCallDispositionsRaw([]);
  }, []);

  const currentOrgId = orgId;

  const value = {
    contacts, deals, tasks, companies, callQueue, activities, customFields, kpiTargets, setKpiTargets, loading, dbReady,
    savedViews, setSavedViews, saveView, deleteView,
    teamMembers, setTeamMembers, updateTeamMember, currentUser, currentUserId, currentUserMember, currentOrgId, getOwnerName,
    stages, stageColors, pipelineStages, setPipelineStages,
    leadStages, setLeadStages,
    customPipelines, setCustomPipelines, customPipelineItems, setCustomPipelineItems,
    addPipeline, updatePipeline, removePipeline, addPipelineItem, updatePipelineItem, removePipelineItem,
    updateContact, getContact, updateDeal, updateDealStage, getDealsForContact, getDealsForCompany,
    updateCompany, deleteCompany, bulkDeleteCompanies, findOrCreateCompany, findOrCreateCompaniesBulk,
    toggleTask, updateTask, addTask, getTasksForDeal, getTasksForContact,
    addActivity, updateActivity, deleteActivity, addNote, logCall, logEmail, logMeeting, getActivitiesForContact, getActivitiesForDeal,
    removeFromQueue, setCallQueue, addToQueue, clearQueue,
    addCustomField, removeCustomField, updateCustomField, setSystemFieldOptions,
    callDispositions, addCallDisposition, updateCallDisposition, toggleCallDisposition, applyDisposition,
    setContacts, setDeals, setTasks, setCompanies, resetData,
    createContact, createDeal, deleteContact, deleteDeal, bulkDeleteDeals, deleteTask,
    softDeleteEmails, restoreEmails, hardDeleteEmails, syncEmails, markEmailRead,
    emailConnections, updateConnectionSharedWith, getAccessibleUserIds,
    dealContacts, addContactToDeal, setPrimaryContact, removeContactFromDeal, getContactsForDeal,
    governanceSettings, setGovernanceSettings, requiredContactFields, setRequiredContactFields,
    requiredDealFields, setRequiredDealFields, stageGates, setStageGates,
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#2C2E33" }}>
        <div style={{ textAlign: "center" }}>
          <img src="/coreflow-logo-white.png" alt="CoreFlow" style={{ height: 28, margin: "0 auto 16px", objectFit: "contain", display: "block" }} />
          <div style={{ fontSize: 12, color: "rgba(250,250,250,0.5)", fontWeight: 400 }}>Loading Coreflow...</div>
        </div>
      </div>
    );
  }

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
